import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/password-input';
import { authClient, meQuery } from '@/lib/auth';
import { safeRedirect } from '@/lib/redirect';

const Search = z.object({
  redirect: z.string().optional(),
  notice: z.enum(['password-set']).optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: Search,
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

const LoginSchema = z.object({
  email: z.email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
});

function LoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: z.infer<typeof LoginSchema>) {
    setError(null);
    const { error: err } = await authClient.signIn.email(values);
    if (err) {
      // Generic on purpose: never reveal whether the email exists.
      setError(
        err.code === 'ACCOUNT_DISABLED'
          ? 'Your account is disabled. Contact an admin.'
          : err.status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : 'Email or password is incorrect.',
      );
      return;
    }
    // Fetch the signed-in user now: an invalidation alone would leave the cached
    // "signed out" value for the layout's guard, which would bounce back here.
    await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
    await navigate({ href: safeRedirect(search.redirect) });
  }

  return (
    <AuthLayout title="Sign in" description="Atlas Control Panel - staff only">
      {search.notice === 'password-set' && (
        <Alert className="mb-4">
          <AlertDescription>Your password is set. Sign in with it now.</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-4" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="username" autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Password</FormLabel>
                  <ForgotPassword defaultEmail={form.getValues('email')} />
                </div>
                <FormControl>
                  <PasswordInput autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  );
}

function ForgotPassword({ defaultEmail }: { defaultEmail: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });
    setBusy(false);
    // Same answer whether or not the account exists.
    setSent(true);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setEmail(defaultEmail);
          setSent(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-4 hover:underline"
        >
          Forgot password?
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {sent
              ? 'If an account exists for that email, a reset link is on its way. It is valid for 24 hours.'
              : 'Enter your staff email and we will send you a reset link.'}
          </DialogDescription>
        </DialogHeader>
        {!sent && (
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@knowaiclub.com"
            aria-label="Email"
          />
        )}
        <DialogFooter>
          {sent ? (
            <Button onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <Button onClick={() => void send()} disabled={busy || !email.includes('@')}>
              {busy ? 'Sending…' : 'Send reset link'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
